import { useState, useRef, useCallback, MutableRefObject } from 'react';
import AssistantQueryService from '@services/AssistantQueryService';
import { IAssistantScript } from '@interfaces/assistantScript';
import { LinearScriptPlayer } from './scriptPlayer/linearScriptPlayer';
import { visualizeAudio } from './AudioVisualizer';
import { log } from './Debug';
import { globals } from './globals';
import { assistantSize } from '@core/assistant/constants';
import { GetEffectiveAiSettingsFn } from './useAssistantScripts';
import { StartFlowFn } from './useScriptFlow';

export type ScriptTransitionCallbacks = {
  setIsScriptCompleted: (v: boolean) => void;
  setPendingScriptStart: (v: null) => void;
  setIsScriptTransitioning: (v: boolean) => void;
};

export type UseAssistantAudioParams = {
  openRef: MutableRefObject<boolean>;
  setOpen: (open: boolean) => boolean;
  linearScriptPlayerRef: MutableRefObject<LinearScriptPlayer | undefined>;
  enqueuedScriptIdRef: MutableRefObject<string | null>;
  scriptsRef: MutableRefObject<IAssistantScript[]>;
  startFlowRef: MutableRefObject<StartFlowFn>;
  stopPlaybackRef: MutableRefObject<() => void>;
  preRenderedAudioRef: MutableRefObject<
    {
      text: string;
      audio: null | ArrayBuffer;
      voiceId: string;
      engine: string;
      language: string;
      area: string;
      index: number;
    }[]
  >;
  scriptTransitionRef: MutableRefObject<ScriptTransitionCallbacks>;
  pendingRegionClearRef: MutableRefObject<boolean>;
  resetAssistantUIRef: MutableRefObject<() => void>;
  getEffectiveAiSettings: GetEffectiveAiSettingsFn;
};

export const useAssistantAudio = ({
  openRef,
  setOpen,
  linearScriptPlayerRef,
  enqueuedScriptIdRef,
  scriptsRef,
  startFlowRef,
  stopPlaybackRef,
  preRenderedAudioRef,
  scriptTransitionRef,
  pendingRegionClearRef,
  resetAssistantUIRef,
  getEffectiveAiSettings,
}: UseAssistantAudioParams) => {
  const [isAudioPlaybackActive, setIsAudioPlaybackActive] = useState(false);
  const [isPollyAudioLoading, setIsPollyAudioLoading] = useState(false);
  const [audioEndTick, setAudioEndTick] = useState(0);

  const isAudioPlaybackActiveRef = useRef<boolean>(false);
  const audioPlaybackIdRef = useRef<number>(0);

  function audioEndCallback(playbackId: number) {
    log('[audio] audioEndCallback fired', {
      playbackId,
      current: audioPlaybackIdRef.current,
    });

    if (playbackId !== audioPlaybackIdRef.current) {
      log(
        '[audio] audioEndCallback STALE — ignoring (stopped manually before natural end)',
      );
      return;
    }
    log('[audio] audioEndCallback NATURAL END');
    isAudioPlaybackActiveRef.current = false;
    // Clear synchronously here rather than relying solely on the branches below:
    // advanceOnAudioEnd combined with an enqueued script (finish_step) matches
    // none of them, so the canvas would otherwise stay visible until the 500ms
    // dequeue timeout runs.
    setIsAudioPlaybackActive(false);
    linearScriptPlayerRef.current?.notifyAudioEnded();

    // bridgeRegionGaps: the user left into an empty region mid-script and we kept
    // playing the current step (see useRegionHandler). The step has now finished
    // naturally — honor the deferred stop instead of auto-advancing to the next
    // step, and reset the UI to default as the empty-region branch would have.
    if (pendingRegionClearRef.current) {
      pendingRegionClearRef.current = false;
      const bridgedPlayerState = linearScriptPlayerRef.current?.getCurrentState();
      log(
        '[audio] bridgeRegionGaps: current step finished after user left region — stopping now',
        {
          area: bridgedPlayerState?.name,
          nodeId: bridgedPlayerState?.currentNode?.id,
        },
      );

      if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
        linearScriptPlayerRef.current?.stop();
      }

      stopPlaybackRef.current();
      enqueuedScriptIdRef.current = null;
      resetAssistantUIRef.current();
      return;
    }

    const linearScriptPlayerState =
      linearScriptPlayerRef.current?.getCurrentState();

    if (
      linearScriptPlayerState?.isRunning &&
      (!linearScriptPlayerState?.currentNode.advanceOnAudioEnd ||
        linearScriptPlayerState?.currentNode.awaitButtonNext)
    ) {
      setIsAudioPlaybackActive(false);
      setAudioEndTick((t) => t + 1);
    }

    if (
      linearScriptPlayerState?.isRunning &&
      linearScriptPlayerState?.currentNode.advanceOnAudioEnd &&
      !linearScriptPlayerState?.currentNode.awaitButtonNext &&
      !enqueuedScriptIdRef.current
    ) {
      setIsAudioPlaybackActive(false);
      log('auto advance script');
      linearScriptPlayerRef.current?.triggerNext();
      log(
        'script current state',
        linearScriptPlayerRef.current?.getCurrentState(),
      );
    }

    log(
      'current node',
      linearScriptPlayerRef.current?.getCurrentState().currentNode.id,
    );

    if (enqueuedScriptIdRef.current) {
      const scriptId = enqueuedScriptIdRef.current;
      enqueuedScriptIdRef.current = null;

      const enqueuedScriptData = scriptsRef.current.find(
        (x) => x.scriptId === scriptId,
      );
      log(
        '[script] finish_step: audio ended naturally, starting enqueued script',
        {
          scriptId,
          playbackPolicy: enqueuedScriptData?.playbackPolicy,
          triggerType: enqueuedScriptData?.triggerType,
          interruptMode: enqueuedScriptData?.interruptMode,
        },
      );

      scriptTransitionRef.current.setIsScriptCompleted(false);
      scriptTransitionRef.current.setPendingScriptStart(null);
      scriptTransitionRef.current.setIsScriptTransitioning(true);

      setTimeout(() => {
        if (linearScriptPlayerRef.current?.getCurrentState().isRunning) {
          linearScriptPlayerRef.current?.stop();
        }

        stopPlaybackRef.current();

        scriptTransitionRef.current.setIsScriptTransitioning(false);
        startFlowRef.current('', { scriptId });
      }, 500);
    }

    // No script player active and no enqueued script (e.g. standalone chat
    // response audio): nothing else will clear the active flag, so do it here.
    if (!linearScriptPlayerState?.isRunning && !enqueuedScriptIdRef.current) {
      setIsAudioPlaybackActive(false);
    }
  }

  const setPlaybackRate = useCallback((rate: number) => {
    globals.assistantPlaybackRate = rate;
    try {
      globals.visualizerInstance?.setPlaybackRate?.(rate);
    } catch (_e) {
      // visualizerInstance may not be initialized yet
    }
  }, []);

  const pauseAudioPlayback = useCallback(() => {
    try {
      globals.visualizerInstance?.pause?.();
    } catch (_e) {
      // visualizerInstance may not be initialized yet
    }
  }, []);

  const resumeAudioPlayback = useCallback(() => {
    try {
      globals.visualizerInstance?.resume?.();
    } catch (_e) {
      // visualizerInstance may not be initialized yet
    }
  }, []);

  const getAudioProgress = useCallback((): {
    currentTime: number;
    duration: number;
  } => {
    try {
      return {
        currentTime: globals.visualizerInstance?.getCurrentTime?.() ?? 0,
        duration: globals.visualizerInstance?.getDuration?.() ?? 0,
      };
    } catch (_e) {
      return { currentTime: 0, duration: 0 };
    }
  }, []);

  const stopPlayback = useCallback(() => {
    log('[audio] stopPlayback', {
      nextPlaybackId: audioPlaybackIdRef.current + 1,
    });
    log('[assistant] stopPlayback caller');
    audioPlaybackIdRef.current++;
    isAudioPlaybackActiveRef.current = false;
    setIsAudioPlaybackActive(false);

    try {
      globals.visualizerInstance.stopAudio();
    } catch (_e) {
      // visualizerInstance may not be initialized yet
    }

    // stopAudio() destroys the underlying AudioVisualizer and closes its
    // AudioContext. Clearing the reference here (rather than leaving it
    // pointing at the now-dead instance) makes pauseAudioPlayback /
    // resumeAudioPlayback / getAudioProgress / setPlaybackRate safely no-op
    // via their existing `globals.visualizerInstance?.x?.()` calls, instead
    // of constructing/connecting nodes on a closed AudioContext (silently
    // does nothing and spams the console). A fresh instance is always
    // created on the next actual playback via onGenerateAudio.
    globals.visualizerInstance = undefined;
  }, []);

  function onGenerateAudio(data: any) {
    log('activeScreen', globals.activeScreenGlob);
    log('isMutedGlob', globals.isMutedGlob);

    if (!globals.isMutedGlob) {
      log('onGenerateAudio data len', data.length);
      isAudioPlaybackActiveRef.current = true;
      log('isAudioPlaybackActiveRef', isAudioPlaybackActiveRef);
      setIsAudioPlaybackActive(true);
      const capturedPlaybackId = audioPlaybackIdRef.current;

      if (globals.visualizerInstance && globals.visualizerInstance.playing) {
        log(
          '[audio] onGenerateAudio stopping previous visualizer before starting new one',
        );
        globals.visualizerInstance.stopAudio();
      }

      log('[audio] onGenerateAudio START', { playbackId: capturedPlaybackId });
      globals.visualizerInstance = visualizeAudio(
        assistantSize.width,
        assistantSize.height,
        () => audioEndCallback(capturedPlaybackId),
      );
      globals.visualizerInstance.initialize(data);
    }
  }

  const generatePollyAudio = async (
    text: string,
    engine?: string,
    voiceId?: string,
    language?: string,
    disableOptimizations = true,
  ) => {
    log('generatePollyAudio muted', globals.isMutedGlob);
    if (globals.isMutedGlob) {
      isAudioPlaybackActiveRef.current = false;
      setIsAudioPlaybackActive(false);
      setAudioEndTick((t) => t + 1);
      linearScriptPlayerRef.current?.notifyAudioEnded();

      const mutedPlayerState = linearScriptPlayerRef.current?.getCurrentState();

      if (
        mutedPlayerState?.isRunning &&
        mutedPlayerState?.currentNode.advanceOnAudioEnd &&
        !mutedPlayerState?.currentNode.awaitButtonNext
      ) {
        linearScriptPlayerRef.current?.triggerNext();
      }

      return;
    }

    if (isAudioPlaybackActiveRef.current && globals.visualizerInstance) {
      log('[audio] generatePollyAudio stopping previous audio for new request');
      globals.visualizerInstance.stopAudio();
    }
    isAudioPlaybackActiveRef.current = false;

    const eff = getEffectiveAiSettings();
    const resolvedEngine = engine ?? eff.engine;
    const resolvedVoiceId = voiceId ?? eff.voiceId;
    const resolvedLanguage = (language as any) ?? eff.language;

    const existingAudio = preRenderedAudioRef.current.find(
      (x) =>
        x.text === text &&
        x.engine === resolvedEngine &&
        x.voiceId === resolvedVoiceId &&
        x.language === resolvedLanguage,
    );

    log('existingAudio', existingAudio);

    if (existingAudio?.audio) {
      log('prerendered audio exists', existingAudio);
      log(
        `Polly audio size before playback: ${(
          existingAudio.audio.byteLength / 1024
        ).toFixed(2)} KB`,
      );
      onGenerateAudio(existingAudio.audio);
      return;
    }

    let arrayBuffer: ArrayBuffer | null = null;

    setIsPollyAudioLoading(true);

    // Capture before the async fetch so we can detect whether stopPlayback()
    // was called while the request was in flight.
    const capturedPlaybackId = audioPlaybackIdRef.current;

    try {
      arrayBuffer = await AssistantQueryService.generateAssistantAudio({
        text,
        disableOptimizations,
        engine: resolvedEngine,
        voiceId: resolvedVoiceId,
        language: resolvedLanguage,
      });
    } catch (e) {
      log('Error: failed to fetch Polly audio', e);
      setIsPollyAudioLoading(false);
      return;
    }

    setIsPollyAudioLoading(false);

    if (audioPlaybackIdRef.current !== capturedPlaybackId) {
      log(
        '[audio] generatePollyAudio — playback stopped during fetch, discarding',
      );
      return;
    }

    // The panel was closed while the fetch was in flight (e.g. user submitted
    // a chat message that implicitly stopped the current script, then navigated
    // away before the audio response arrived).
    if (!openRef.current) {
      log('[audio] generatePollyAudio — panel closed during fetch, discarding');
      return;
    }

    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      log('Error: empty or missing Polly audio response');
      return;
    }

    log(
      `Polly audio size before playback: ${(
        arrayBuffer.byteLength / 1024
      ).toFixed(2)} KB`,
    );

    if (existingAudio) {
      existingAudio.audio = arrayBuffer;
    }

    onGenerateAudio(arrayBuffer);
  };

  const playBlockAudio = useCallback(
    async (
      text: string,
      engine?: string,
      voiceId?: string,
      language?: string,
    ) => {
      log('playback block text ->', text);

      if (!openRef.current) {
        const didOpen = setOpen(true);

        if (!didOpen) {
          // Opening was suppressed (e.g. editor/branding mode) — skip playback
          // too, otherwise Noa speaks with no visible avatar.
          log(
            '[audio] playBlockAudio — assistant open suppressed, skipping playback',
          );
          return;
        }

        setTimeout(() => {
          generatePollyAudio(text, engine, voiceId, language, false);
        }, 100);
        return;
      }
      generatePollyAudio(text, engine, voiceId, language, false);
    },
    [],
  );

  const startAudioPrerender = async (
    currentArea: string,
    startStep: number,
  ) => {
    const capturedPlaybackId = audioPlaybackIdRef.current;
    const preRenderedAudioScript = preRenderedAudioRef.current.filter(
      (x) => x.area === currentArea && !x.audio,
    );
    log(
      'startAudioPrerender audio ' + currentArea,
      preRenderedAudioScript.length,
    );

    for (let i = startStep; i < preRenderedAudioScript.length; i++) {
      if (audioPlaybackIdRef.current !== capturedPlaybackId) {
        log('[audio] startAudioPrerender aborted — playback stopped');
        break;
      }
      log('startAudioPrerender audio ' + i);

      try {
        const arrayBuffer = await AssistantQueryService.generateAssistantAudio({
          text: preRenderedAudioScript[i].text,
          disableOptimizations: true,
          engine: preRenderedAudioScript[i].engine,
          voiceId: preRenderedAudioScript[i].voiceId,
          language: preRenderedAudioScript[i].language,
        });

        if (!arrayBuffer || arrayBuffer.byteLength === 0) {
          log('Error: empty or missing Polly audio response for step ' + i);
          continue;
        }

        preRenderedAudioScript[i].audio = arrayBuffer;
        log(
          'startAudioPrerender audio ' + i,
          `${(arrayBuffer.byteLength / 1024).toFixed(2)} KB`,
        );
      } catch (e) {
        log('Error: failed to fetch Polly audio for step ' + i, e);
      }
    }

    log('preRenderedAudioScript', preRenderedAudioScript);
  };

  const clearPrerenderedAudio = useCallback(() => {
    preRenderedAudioRef.current = [];
    log('prerendered audio cleared');
  }, []);

  return {
    isAudioPlaybackActive,
    isPollyAudioLoading,
    audioEndTick,
    isAudioPlaybackActiveRef,
    audioPlaybackIdRef,
    generatePollyAudio,
    playBlockAudio,
    stopPlayback,
    clearPrerenderedAudio,
    startAudioPrerender,
    setPlaybackRate,
    getAudioProgress,
    pauseAudioPlayback,
    resumeAudioPlayback,
  };
};
